import { useEffect, useState } from "react";

export type TrackingMode = "restriction" | "flex" | "balance" | "none";

const STORAGE_KEY = "p2p_trackingMode";

export const TRACKING_MODE_LABELS: Record<TrackingMode, string> = {
  restriction: "Restriktiv",
  flex: "Flex-Bereich",
  balance: "Balance Score",
  none: "Kein Tracking",
};

export const TRACKING_MODE_DESCRIPTIONS: Record<TrackingMode, string> = {
  restriction: "Nährwerte werden täglich genau gegen den Bedarf gemessen.",
  flex: "Wöchentliches Budget mit ±12,5 % Schwankung – gibt dir Flexibilität über die Woche.",
  balance:
    "Bewertet, wie ausgewogen deine Ernährung an diesem Tag ist (0–100 Punkte).",
  none: "Keine Nährwert-Anzeige.",
};

/** 12,5 % Spielraum nach oben und unten für den Flex-Bereich */
export const FLEX_MARGIN = 0.125;

export function useTrackingMode(): [
  TrackingMode,
  (mode: TrackingMode) => void,
] {
  const [mode, setMode] = useState<TrackingMode>(
    () => (localStorage.getItem(STORAGE_KEY) as TrackingMode) ?? "restriction",
  );

  function updateMode(newMode: TrackingMode) {
    setMode(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
  }

  return [mode, updateMode];
}

const SHOW_HINTS_STORAGE_KEY = "p2p_showHints";
const HINTS_OFF_CLASS = "p2p-hints-off";

// Beim Laden synchron anwenden, damit ausgeblendete Hinweise nicht kurz
// aufblitzen, bevor React gerendert/Effects ausgeführt hat.
if (
  typeof document !== "undefined" &&
  localStorage.getItem(SHOW_HINTS_STORAGE_KEY) === "false"
) {
  document.documentElement.classList.add(HINTS_OFF_CLASS);
}

/**
 * Globale Einstellung, ob Hinweistexte (Komponente {@link Hint}) angezeigt
 * werden. Default: an.
 */
export function useShowHints(): [boolean, (show: boolean) => void] {
  const [showHints, setShowHints] = useState<boolean>(
    () => localStorage.getItem(SHOW_HINTS_STORAGE_KEY) !== "false",
  );

  useEffect(() => {
    document.documentElement.classList.toggle(HINTS_OFF_CLASS, !showHints);
  }, [showHints]);

  function updateShowHints(show: boolean) {
    setShowHints(show);
    localStorage.setItem(SHOW_HINTS_STORAGE_KEY, String(show));
  }

  return [showHints, updateShowHints];
}
